/**
 * A wrapper to handle the Modern pagination style, and infinite scroll.
 *
 * Usage:
 *  {%- if settings.pagination_style == "modern" or settings.pagination_infinite -%}
 *    <script src="{{ 'custom-pagination.js' | asset_url }}" defer="defer"></script>
 *  {%- endif -%}
 *
 *  {%- paginate thing by stuff -%}
 *    <custom-pagination data-infinite-scroll="{{ settings.pagination_infinite }}"
 *      data-pause-infinite-scroll="false">
 *      <ul>
 *        <li class="js-pagination-result">One</li>
 *        <li class="js-pagination-result">Two</li>
 *        <li class="js-pagination-result">Three</li>
 *      </ul>
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate, style: settings.pagination_style %}
 *      {%- endif -%}
 *    </custom-pagination>
 *  {%- endpaginate -%}
 */
if (!customElements.get('custom-pagination')) {
  /**
   * Regios Discounts (DOPP): re-run discounts on infinite-scroll / load-more rows.
   * Reads attributes from the already-parsed product-card nodes so we never re-serialize
   * and re-parse the appended markup just to collect ids.
   * @see https://regiostech.crisp.help/en/article/dopp-api-examples-1p830j3/
   * @param {Element[]|NodeList} resultNodes - Newly appended .js-pagination-result nodes.
   * @returns {Array<{productId:number,variantId:number,regularPriceInCents:number}>}
   */
  function regiosNewItemsFromNodes(resultNodes) {
    const items = [];
    if (!resultNodes) return items;
    resultNodes.forEach((node) => {
      node
        .querySelectorAll('product-card[data-product-id][data-variant-id][data-regular-price-cents]')
        .forEach((card) => {
          const productId = parseInt(card.getAttribute('data-product-id'), 10);
          const variantId = parseInt(card.getAttribute('data-variant-id'), 10);
          const regularPriceInCents = parseInt(card.getAttribute('data-regular-price-cents'), 10);
          if (
            Number.isInteger(productId) &&
            productId > 0 &&
            Number.isInteger(variantId) &&
            variantId > 0 &&
            Number.isInteger(regularPriceInCents) &&
            regularPriceInCents >= 0
          ) {
            items.push({ productId, variantId, regularPriceInCents });
          }
        });
    });
    return items;
  }

  /**
   * Copies data-src/data-srcset to src/srcset for lazy images within the given nodes,
   * mirroring the global initLazyImages() swap but scoped to the freshly appended rows so
   * we don't rescan the whole (growing) document on every page (was O(n^2)).
   * @param {Element[]|NodeList} nodes - Newly appended result nodes.
   */
  function initLazyImagesInNodes(nodes) {
    if (!nodes) return;

    const setSrc = (el) => {
      if (el.dataset.src && !el.src) el.src = el.dataset.src;
      if (el.dataset.srcset && !el.srcset) el.srcset = el.dataset.srcset;
    };
    const swap = (img) => {
      if (img.parentNode && img.parentNode.tagName === 'PICTURE') {
        Array.from(img.parentNode.children).forEach(setSrc);
      } else {
        setSrc(img);
      }
    };

    const nativeLazy = 'loading' in HTMLImageElement.prototype;
    const useObserver = !nativeLazy && 'IntersectionObserver' in window;
    const io = useObserver
      ? new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            swap(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px 500px 0px' })
      : null;

    nodes.forEach((node) => {
      node.querySelectorAll('[loading="lazy"]').forEach((img) => {
        if (io) io.observe(img);
        else swap(img);
      });
    });
  }

  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.init();
      document.addEventListener('on:bfcache:load-restore', this.reload.bind(this));
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      // Track the tail incrementally so we never re-query the whole growing tree per page.
      this.resultCount = this.results.length;
      this.lastResultEl = this.results[this.results.length - 1] || null;
      this.controller = null;

      // Bind click handlers to pagination results
      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.addEventListener('click', this.resultClickHandler);

      // Bind click handler to load more button click
      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);

      if (this.pagination && this.pagination.dataset.isMoreResults === 'true' && this.results) {
        if (this.loadMoreButton) this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);

        if (this.infiniteScroll) {
          this.paginationObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) this.loadMoreSequential();
              });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
          );

          this.paginationObserver.observe(this.pagination);
        }
      }
    }

    /**
     * Destroys the component
     */
    destroy() {
      if (this.intersectingTimer) clearInterval(this.intersectingTimer);
      if (this.paginationObserver) this.paginationObserver.disconnect();
      if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.removeEventListener('click', this.resultClickHandler);
      this.loadMoreClickHandler = null;
    }

    /**
     * Reloads the component
     */
    reload() {
      this.destroy();
      this.init();
    }

    /**
     * Handles click on the load more button
     * @param {object} evt - Event object
     */
    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.loadMore();
    }

    /**
     * Handles a click on a item in the pagination
     * @param {object} evt - Event object
     */
    handleResultClick(evt) {
      if (evt.target.closest('a') && evt.target.closest('.js-pagination-result') && this.controller) {
        this.controller.abort();
      }
    }

    /**
     * Builds the load-more request URL, adding section_id (Section Rendering API) derived
     * from the enclosing .shopify-section so the server returns only this section's markup
     * rather than the entire page document. Falls back to the raw href if no section is found.
     * @param {string} href - The pagination "next" link href.
     * @returns {string}
     */
    buildLoadMoreUrl(href) {
      try {
        const url = new URL(href, window.location.origin);
        const section = this.closest('.shopify-section');
        const sectionId = section && section.id ? section.id.replace(/^shopify-section-/, '') : '';
        if (sectionId) url.searchParams.set('section_id', sectionId);
        return url.toString();
      } catch (e) {
        return href;
      }
    }

    /**
     * Fetches more results from the next page of results, and dynamically updates this page
     */
    async loadMore() {
      if (!this.isFetching && this.dataset.pauseInfiniteScroll === 'false') {
        this.isFetching = true;
        this.controller = new AbortController();

        const paginationMessageEl = this.pagination?.querySelector('.js-pagination-message');
        const paginationMessagePrev = paginationMessageEl ? paginationMessageEl.textContent : '';
        if (paginationMessageEl) {
          paginationMessageEl.textContent = 'Loading Products';
        }

        try {
          // Add the loading state to the button
          if (!this.loadMoreButton) throw new Error("No 'Load more' button found.");

          this.loadMoreButton.classList.add('is-loading');
          this.loadMoreButton.setAttribute('aria-disabled', 'true');

          if (this.pagination?.dataset?.paginationStyle === 'traditional') {
            this.pagination.classList.add('is-loading');
          }

          // Retrieve and parse the next page. Request only this section (Section Rendering
          // API) instead of the full page document so the response is a fraction of the size
          // and far cheaper to parse.
          const response = await fetch(this.buildLoadMoreUrl(this.loadMoreButton.href), {
            signal: this.controller.signal
          });

          if (!response.ok) throw new Error(response.status);
          if (this.dataset.pauseInfiniteScroll === 'true') {
            throw new Error('Infinite scroll was interrupted.');
          }

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Move the parsed nodes straight into the DOM via a fragment - no outerHTML
          // string round-trip and no second/third reparse of the same markup.
          const newResultNodes = Array.from(tmpl.content.querySelectorAll('.js-pagination-result'));
          const anchor = this.lastResultEl || this.results[this.results.length - 1];

          if (anchor && newResultNodes.length) {
            const frag = document.createDocumentFragment();
            newResultNodes.forEach((node) => frag.appendChild(node));
            anchor.after(frag);

            // Lazy-load only the freshly inserted images, not the whole growing document.
            initLazyImagesInNodes(newResultNodes);

            // Advance the tail pointers instead of re-querying the entire tree.
            this.lastResultEl = newResultNodes[newResultNodes.length - 1];
            this.resultCount += newResultNodes.length;

            CustomPagination.notifyRegiosDoppNewItems(newResultNodes);
          }

          // Update the pagination content
          const newPagination = tmpl.content.querySelector('.js-pagination');
          // data-is-more-results is always a string; "false" is truthy in JS — compare explicitly
          const hasMorePages = newPagination?.dataset?.isMoreResults === 'true';

          if (hasMorePages) {
            this.pagination.innerHTML = newPagination.innerHTML;

            // Rebind to the new load more button
            if (this.loadMoreButton) {
              this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
              this.loadMoreButton = this.querySelector('.js-pagination-load-more');
              if (this.loadMoreButton) {
                this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
              }
            }
          } else {
            this.pagination.remove();
            this.pagination = null;
            if (this.intersectingTimer) clearInterval(this.intersectingTimer);
            if (this.paginationObserver) this.paginationObserver.disconnect();
            document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());
          }
        } catch (error) {
          console.log('Unable to fetch next page of results', error); // eslint-disable-line
          if (this.intersectingTimer) clearInterval(this.intersectingTimer);
          if (paginationMessageEl && paginationMessageEl.isConnected && paginationMessagePrev) {
            paginationMessageEl.textContent = paginationMessagePrev;
          }
        } finally {
          this.controller = null;

          // Remove the loading state from the load more button
          if (this.loadMoreButton) {
            this.loadMoreButton.classList.remove('is-loading');
            this.loadMoreButton.removeAttribute('aria-disabled');
          }

          this.isFetching = false;

          if (this.pagination?.dataset?.paginationStyle === 'traditional') {
            this.pagination.classList.remove('is-loading');
          }
        }
      }
    }

    /**
     * Loads pages one at a time while the sentinel stays near the viewport, instead of
     * a blind setInterval that could stack fetches and auto-load every page (DOM blowup).
     */
    async loadMoreSequential() {
      if (this.isLoadingSequential) return;
      this.isLoadingSequential = true;

      try {
        while (
          this.pagination &&
          this.dataset.pauseInfiniteScroll === 'false' &&
          this.isSentinelNearViewport()
        ) {
          const prevCount = this.resultCount;
          await this.loadMore();
          // Stop if a page removed pagination (no more pages) or nothing new was appended
          // (e.g. a failed fetch) so we never spin in a runaway/error loop.
          if (!this.pagination || this.resultCount <= prevCount) break;
        }
      } finally {
        this.isLoadingSequential = false;
      }
    }

    /**
     * @returns {boolean} true if the pagination sentinel is within ~1.5 viewports.
     */
    isSentinelNearViewport() {
      if (!this.pagination) return false;
      const rect = this.pagination.getBoundingClientRect();
      const margin = window.innerHeight * 1.5;
      return rect.top <= window.innerHeight + margin && rect.bottom >= -margin;
    }

    /**
     * @param {Element[]|NodeList} newResultNodes - Newly appended .js-pagination-result nodes.
     */
    static notifyRegiosDoppNewItems(newResultNodes) {
      queueMicrotask(() => {
        try {
          const newItems = regiosNewItemsFromNodes(newResultNodes);
          if (!newItems.length) return;
          window.dispatchEvent(
            new CustomEvent('regios-dopp:collection-page:new-items', {
              bubbles: true,
              detail: { uniqueKey: 'default', newItems }
            })
          );
        } catch (e) {
          /* Regios optional */
        }
      });
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}
